import React, { useEffect, useState } from 'react';
import { Download, LogOut, Lock, RefreshCw, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'momentedge_admin_auth';

const authHeader = (token) => ({ Authorization: `Basic ${token}` });

const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const token = btoa(`${username}:${password}`);
    try {
      const res = await fetch(`${API}/api/subscribers/count`, { headers: authHeader(token) });
      if (res.status === 401) throw new Error('Invalid username or password');
      if (!res.ok) throw new Error('Unable to reach the server');
      sessionStorage.setItem(STORAGE_KEY, token);
      onLogin(token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl p-8 md:p-10" data-testid="admin-login-card">
      <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center text-slate-900 mb-6">
        <Lock size={24} />
      </div>
      <h1 className="text-3xl font-bold text-white mb-2">Admin Login</h1>
      <p className="text-slate-400 mb-8">Sign in to view and export whitepaper leads.</p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="admin-username" className="block text-sm font-medium text-slate-300 mb-2">Username</label>
          <Input
            id="admin-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoComplete="username"
            data-testid="admin-username-input"
            className="w-full bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label htmlFor="admin-password" className="block text-sm font-medium text-slate-300 mb-2">Password</label>
          <Input
            id="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            data-testid="admin-password-input"
            className="w-full bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
          />
        </div>
        {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full bg-white text-slate-900 hover:bg-slate-100 font-semibold py-3">
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>
      </form>
    </div>
  );
};

const formatDate = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const LeadsTable = ({ token, onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/subscribers`, { headers: authHeader(token) });
      if (res.status === 401) return onLogout();
      if (!res.ok) throw new Error('Failed to load leads');
      setLeads(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    const res = await fetch(`${API}/api/subscribers/export`, { headers: authHeader(token) });
    if (!res.ok) return setError('Export failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `momentedge_leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-leads-view">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Whitepaper Leads</h1>
          <p className="text-slate-400 inline-flex items-center gap-2">
            <Users size={16} />
            <span data-testid="admin-leads-count">{leads.length} total</span>
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={load} variant="outline" data-testid="admin-refresh-button" className="border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white">
            <RefreshCw size={16} className="mr-2" /> Refresh
          </Button>
          <Button onClick={exportCsv} data-testid="admin-export-button" className="bg-white text-slate-900 hover:bg-slate-100 font-semibold">
            <Download size={16} className="mr-2" /> Export CSV
          </Button>
          <Button onClick={onLogout} variant="outline" data-testid="admin-logout-button" className="border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white">
            <LogOut size={16} className="mr-2" /> Sign Out
          </Button>
        </div>
      </div>

      {error && <p className="text-red-400 mb-4" data-testid="admin-leads-error">{error}</p>}

      <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
        <table className="w-full text-left text-sm" data-testid="admin-leads-table">
          <thead className="bg-slate-900/60 text-slate-400 uppercase text-xs tracking-wider">
            <tr>
              <th className="px-6 py-4">Email</th>
              <th className="px-6 py-4">Whitepaper</th>
              <th className="px-6 py-4">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50 text-slate-200">
            {loading ? (
              <tr><td colSpan={3} className="px-6 py-10 text-center text-slate-400">Loading...</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={3} className="px-6 py-10 text-center text-slate-400" data-testid="admin-leads-empty">No leads yet.</td></tr>
            ) : (
              leads.map((lead) => (
                <tr key={lead.id} className="hover:bg-slate-800/60 transition-colors" data-testid="admin-lead-row">
                  <td className="px-6 py-4 font-medium text-white">{lead.email}</td>
                  <td className="px-6 py-4 text-slate-300">{lead.whitepaper}</td>
                  <td className="px-6 py-4 text-slate-400 whitespace-nowrap">{formatDate(lead.timestamp)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY));

  useEffect(() => {
    document.title = 'Admin | Momentedge';
    const robots = document.querySelector('meta[name="robots"]');
    const prev = robots?.getAttribute('content');
    robots?.setAttribute('content', 'noindex, nofollow');
    return () => { if (robots && prev) robots.setAttribute('content', prev); };
  }, []);

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken(null);
  };

  return (
    <div className="min-h-screen pt-20" data-testid="admin-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"></div>
      </div>
      <section className="relative py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {token ? <LeadsTable token={token} onLogout={logout} /> : <AdminLogin onLogin={setToken} />}
        </div>
      </section>
    </div>
  );
};

export default Admin;
